import { jsPDF } from "jspdf";

const BLUE = [124, 44, 191];

const mdy = (s) => { if (!s) return ""; const p = String(s).split("-"); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : s; };

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function generateQuotePdf(quote) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 48;
  let y = 56;

  doc.setFillColor(...BLUE);
  doc.rect(0, 0, W, 8, "F");

  try {
    const logo = await loadImage("/brandbloom-logo.png");
    const lw = 170;
    const lh = (lw * 375) / 1531;
    doc.addImage(logo, "PNG", margin, y - 24, lw, lh);
  } catch {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(26);
    doc.setTextColor(10, 10, 10);
    doc.text("Brand Bloom", margin, y);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text(`Quote #${(quote.id || "").slice(-8).toUpperCase()}`, W - margin, y, { align: "right" });
  doc.text(new Date(quote.created_at || Date.now()).toLocaleDateString(), W - margin, y + 16, { align: "right" });

  y += 50;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, W - margin, y);

  y += 28;
  doc.setTextColor(10, 10, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("PREPARED FOR", margin, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  y += 18;
  doc.text(quote.client_name || "", margin, y);
  if (quote.company) { y += 15; doc.text(quote.company, margin, y); }
  y += 15;
  doc.setTextColor(120, 120, 120);
  doc.text(quote.client_email || "", margin, y);
  if (quote.expected_completion) {
    y += 15;
    doc.setTextColor(...BLUE);
    doc.text(`Expected completion: ${mdy(quote.expected_completion)}`, margin, y);
  }

  y += 36;
  doc.setTextColor(10, 10, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("LINE ITEMS", margin, y);
  y += 10;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, W - margin, y);
  y += 6;

  doc.setFontSize(10);
  (quote.line_items || []).forEach((li) => {
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(10, 10, 10);
    doc.text(li.name, margin, y);
    const price = `$${li.price.toLocaleString()}${li.billing === "monthly" ? " /mo" : ""}`;
    doc.text(price, W - margin, y, { align: "right" });
    doc.setTextColor(160, 160, 160);
    doc.setFontSize(8);
    doc.text(li.billing.toUpperCase(), margin, y + 11);
    doc.setFontSize(10);
  });

  y += 30;
  doc.setDrawColor(10, 10, 10);
  doc.line(margin, y, W - margin, y);
  y += 24;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(10, 10, 10);
  doc.text("One-time total", margin, y);
  doc.text(`$${(quote.one_time_total || 0).toLocaleString()}`, W - margin, y, { align: "right" });
  if (quote.monthly_total > 0) {
    y += 22;
    doc.text("Monthly total", margin, y);
    doc.setTextColor(...BLUE);
    doc.text(`$${quote.monthly_total.toLocaleString()} /mo`, W - margin, y, { align: "right" });
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  doc.text("Thank you for considering Quotient. This quote is valid for 10 days.", margin, doc.internal.pageSize.getHeight() - 40);

  doc.save(`quotient-quote-${(quote.id || "").slice(-6)}.pdf`);
}

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PDF_PUNCT = { "\u2014": "-", "\u2013": "-", "\u2018": "'", "\u2019": "'", "\u201c": '"', "\u201d": '"', "\u2026": "...", "\u2022": "-", "\u00a0": " ", "\u2192": "->", "\u2713": "-" };
function sanitizePdfText(t) {
  let out = t || "";
  for (const [k, v] of Object.entries(PDF_PUNCT)) out = out.split(k).join(v);
  return out.split("").filter((ch) => ch.charCodeAt(0) < 256).join("");
}

export async function generateProposalPdf(quote) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 56;
  const maxW = W - margin * 2;
  let y = 64;
  const ensure = (h) => { if (y + h > H - 64) { doc.addPage(); y = 64; } };

  doc.setFillColor(...BLUE);
  doc.rect(0, 0, W, 8, "F");
  try {
    const logo = await loadImage("/brandbloom-logo.png");
    const lw = 180, lh = (lw * 375) / 1531;
    doc.addImage(logo, "PNG", margin, y - 24, lw, lh);
  } catch {
    doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(10, 10, 10);
    doc.text("Brand Bloom", margin, y);
  }
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120, 120, 120);
  doc.text(`Proposal #${(quote.id || "").slice(-8).toUpperCase()}`, W - margin, y - 8, { align: "right" });
  doc.text(new Date(quote.created_at || Date.now()).toLocaleDateString(), W - margin, y + 6, { align: "right" });
  y += 48;

  doc.setFont("helvetica", "bold"); doc.setFontSize(26); doc.setTextColor(10, 10, 10);
  doc.text("Project Proposal", margin, y); y += 22;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(90, 90, 90);
  doc.text(`Prepared for ${quote.client_name || ""}${quote.company ? " · " + quote.company : ""}`, margin, y); y += 14;
  if (quote.client_email) { doc.text(quote.client_email, margin, y); y += 14; }
  if (quote.expected_completion) { doc.setTextColor(...BLUE); doc.text(`Expected completion: ${mdy(quote.expected_completion)}`, margin, y); doc.setTextColor(90, 90, 90); y += 14; }
  y += 10;
  doc.setDrawColor(220, 220, 220); doc.line(margin, y, W - margin, y); y += 26;

  const lines = sanitizePdfText(quote.proposal || "").split("\n");
  for (const raw of lines) {
    if (raw.trim() === "") { y += 8; continue; }
    if (/^#{1,6}\s/.test(raw)) {
      const text = raw.replace(/^#+\s/, "").replace(/\*\*/g, "");
      const wrapped = doc.splitTextToSize(text, maxW);
      ensure(wrapped.length * 18 + 12); y += 10;
      doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...BLUE);
      doc.text(wrapped, margin, y); y += wrapped.length * 18 + 4;
    } else {
      const bullet = /^\s*[-*]\s+/.test(raw);
      const text = raw.replace(/\*\*/g, "").replace(/^\s*[-*]\s+/, "").trim();
      const x = bullet ? margin + 14 : margin;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor(40, 40, 40);
      const wrapped = doc.splitTextToSize(text, maxW - (bullet ? 14 : 0));
      ensure(wrapped.length * 15 + 4);
      if (bullet) { doc.setTextColor(...BLUE); doc.text("•", margin, y); doc.setTextColor(40, 40, 40); }
      doc.text(wrapped, x, y); y += wrapped.length * 15 + 3;
    }
  }

  ensure(170); y += 18;
  doc.setDrawColor(10, 10, 10); doc.line(margin, y, W - margin, y); y += 24;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(10, 10, 10);
  doc.text("Investment", margin, y); y += 20;
  doc.setFontSize(10.5);
  (quote.line_items || []).forEach((li) => {
    ensure(20);
    doc.setFont("helvetica", "normal"); doc.setTextColor(40, 40, 40);
    doc.text(li.name, margin, y);
    doc.text(`${money(li.price)}${li.billing === "monthly" ? " /mo" : ""}`, W - margin, y, { align: "right" });
    y += 18;
  });
  ensure(50); y += 6;
  doc.setDrawColor(220, 220, 220); doc.line(margin, y, W - margin, y); y += 20;
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(10, 10, 10);
  doc.text("One-time total", margin, y);
  doc.text(money(quote.one_time_total), W - margin, y, { align: "right" });
  if (quote.monthly_total > 0) {
    y += 18;
    doc.setTextColor(10, 10, 10); doc.text("Monthly total", margin, y);
    doc.setTextColor(...BLUE); doc.text(`${money(quote.monthly_total)} /mo`, W - margin, y, { align: "right" });
  }

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(150, 150, 150);
    doc.text("Brand Bloom · Web · SEO · Social — Proposal valid for 10 days", margin, H - 30);
    doc.text(`${i} / ${pages}`, W - margin, H - 30, { align: "right" });
  }

  doc.save(`brandbloom-proposal-${(quote.id || "").slice(-6)}.pdf`);
}
